import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import { ReplaySubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { IGroup } from '../../groups/IGroups';
import {
    flatDeep,
    IStatusMessage,
    Process,
    ResponseStatus
} from '../../shared';
import { SelectItem, Table } from '../../shared/efaact-primeng.module';
import { IShowErrors } from '../../shared/errors-sidenav/IShowErrors';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ReminderAddComponent } from '../reminder-add/reminder-add.component';
import { Reminder } from '../store/reminder.model';

@Component({
    selector: 'ef-reminder',
    templateUrl: './reminder.component.html',
    styleUrls: ['./reminder.component.scss'],
    standalone: false
})
export class ReminderComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() items: Reminder[];
    @Input() reminderTypes: SelectItem[];
    @Input() reminderScheduleTypes: SelectItem[];
    @Input() recipients: IGroup[];
    @Input() reminderReviewPeriods: SelectItem[];
    @Input() reminderGroups: Array<{ id: number; groups: number[] }>;
    @Input() process: Process;
    @Input() reminderStatus: boolean;
    private _selectedError: IStatusMessage;
    @Input() public set selectedError(value: IStatusMessage) {
        if (!value) {
            return;
        }
        if (this._selectedError) {
            const oldRow = document.getElementById(
                `row-${this._selectedError.MsgEntityID.toString()}`
            );
            if (oldRow) {
                oldRow.classList.remove('highlight');
                oldRow.classList.remove('highlight-warning');
                oldRow.classList.remove('highlight-danger');
            }
        }
        this._selectedError = value;
        if (!(value && value.MsgEntityID)) {
            return;
        }
        const row = document.getElementById(
            `row-${value.MsgEntityID.toString()}`
        );
        this.log.verbose({
            type: 'PayrollItemComponent:selectedError',
            value,
            row
        });
        if (!row) {
            return;
        }
        row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        row.classList.add('highlight');
        if (value.StatusCode === ResponseStatus.Warning) {
            row.classList.add('highlight-warning');
        }
        if (value.StatusCode === ResponseStatus.Failed) {
            row.classList.add('highlight-danger');
        }
    }
    @Output() public ShowAdd = new EventEmitter();
    remindersToggleForm: UntypedFormGroup;
    public moment: any;
    public editing: Reminder = {} as Reminder;
    @Output() public ShowError = new ReplaySubject<IShowErrors>(1);
    @Output() SaveReminder = new ReplaySubject<Reminder>(1);
    @Output() ApplyAction = new EventEmitter<Reminder>();
    @Output() ToggleReminders = new ReplaySubject<boolean>(1);
    @Output() AddReminder = new EventEmitter<Reminder>();
    @Output() RunNow = new EventEmitter<Reminder>();
    @ViewChild('dtReminder', { static: true }) reminderTable: Table;
    @ViewChild('addReminder', { static: true })
    addReminderComponent: ReminderAddComponent;
    cloned: { [i: number]: Reminder } = {};
    private active = true;
    constructor(
        private log: LoggingService,
        private confirmservice: UnsavedChangesService
    ) {
        this.moment = moment;
        this.remindersToggleForm = new UntypedFormGroup({
            remindersEnabled: new UntypedFormControl()
        });
    }
    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.active = false;
    }
    ngAfterViewInit() {
        this.remindersToggleForm.controls['remindersEnabled'].valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(value => {
                this.ToggleReminders.next(value);
            });
    }

    onEditInit($event: { data: Reminder }) {
        const reminder = this.items.find(
            x => x.Reminderinstanceid === $event.data.Reminderinstanceid
        );
        this.log.verbose({
            type: 'ReminderComponent:onEditInit',
            $event,
            editing: this.editing,
            setting: reminder
        });
        this.editing = { ...$event.data };
    }
    onEditComplete($event: { data: Partial<Reminder> }) {
        this.log.verbose({
            type: 'ReminderComponent:onEditComplete',
            $event
        });
        const reminder = this.items.find(
            x => x.Reminderinstanceid === $event.data.Reminderinstanceid
        );

        const newVal = reminder;
        this.log.verbose({
            type: 'ReminderComponent:onEditComplete',
            $event,
            setting: reminder,
            editing: this.editing
        });
        if (newVal === this.editing) {
            // this.editing = null;
            return;
        }
        this.SaveReminder.next(newVal);
    }
    onRowEditInit(item: Reminder) {
        this.log.verbose({
            type: 'ReminderComponent:onRowEditInit',
            r: item,
            clonedReminders: this.cloned
        });
        this.cloned[item.Reminderinstanceid] = { ...item };
        this.editing = item;
    }
    onRowEditSave(item: Reminder) {
        this.log.verbose({
            type: 'ReminderComponent:onRowEditSave',
            r: item,
            clonedReminders: this.cloned
        });
        item.Status.StatusCode = ResponseStatus.Loading;
        item.Status.Message = 'Saving';
        this.SaveReminder.next(item);
        delete this.cloned[item.Reminderinstanceid];
        delete this.editing;
    }
    onRowEditCancel(item: Reminder) {
        this.log.verbose({
            type: 'ReminderComponent:onRowEditCancel',
            r: item,
            clonedReminders: this.cloned
        });

        const index = this.items.findIndex(
            x => x.Reminderinstanceid === item.Reminderinstanceid
        );
        if (index >= 0) {
            this.items.splice(index, 1, this.cloned[item.Reminderinstanceid]);
        }
        this.items = [...this.items];

        delete this.cloned[item.Reminderinstanceid];
        delete this.editing;
    }
    confirm(emitter: EventEmitter<Reminder>, reminder: Reminder) {
        this.confirmservice
            .confirmPromise({
                dialogText: 'Are you sure you want to delete this reminder?'
            })
            .then(() => {
                emitter.emit(reminder);
            })
            .catch(() => {});
    }
    onDelete(r: Reminder) {
        this.confirm(this.ApplyAction, r);
        this.log.verbose({
            type: 'ReminderComponent:onDelete',
            r
        });
    }
    onApplyAction(r: Reminder) {
        this.log.verbose({
            type: 'ReminderComponent:onApplyAction',
            r,
            clonedReminders: this.cloned
        });
        this.ApplyAction.next(r);
    }
    groupsForReminderType(typeId: number) {
        const groups = flatDeep<number>(
            this.reminderGroups.filter(y => y.id === typeId).map(x => x.groups)
        ) as number[];
        const results = this.recipients
            .filter(x => groups.includes(x.GroupID) || x.IsRole === false)
            .map(x => ({ label: x.GroupName, value: x.GroupID } as SelectItem));
        this.log.verbose({
            type: 'ReminderComponent:groupsForReminderType',
            typeId,
            groups,
            reminderGroups: this.reminderGroups,
            recipients: this.recipients,
            results
        });
        return results;
    }
}
